import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PlayFab } from './services/PlayFab';
import { Account } from './services/Account';
import { format } from 'date-fns';
import './HandHistoryPage.css'; // Reuse styles
import { HandHistoryCard } from './components/HandHistoryCard';
import './SessionHandsPage.css'; // Add specific styles if needed

// Define HandSummary interface (or import from types file)
interface HandSummary {
  handId: string;
  timestamp: string;
  heroCards: { holeCard1: string; holeCard2: string } | null;
  potSize: number;
  overallRating?: number;
  streetRatings?: { [key: string]: number };
  handType?: string;
  SK?: string;
  summaryJson?: any; // Or a more specific type if known
  heroProfitBB?: number;
}

// Helper function (or import)
const parseCard = (cardStr: string): { rank: string; suit: string } | null => {
    if (!cardStr || cardStr.length !== 2) return null;
    return { rank: cardStr[0], suit: cardStr[1] };
};

// Helper function (or import)
const getRatingCategory = (rating: number): string => {
  if (rating >= 9) return 'excellent';
  if (rating >= 7) return 'good';
  if (rating >= 4) return 'okay';
  if (rating >= 1) return 'poor';
  return 'unknown';
};

// --- Add Sort State Type ---
type SortKey = 'timestamp' | 'potSize' | 'heroProfitBB' | 'overallRating';
type SortDirection = 'asc' | 'desc';

// --- Add Rating Filter Type ---
type RatingFilter = 'all' | 'excellent' | 'good' | 'okay' | 'poor' | 'unknown';

interface SessionHandsPageState {
  sessionHands: HandSummary[];
  loading: boolean;
  error: string | null;
  isWaitingForAnalysis: boolean;
  isTriggeringAnalysis: boolean;
  analysisTriggerMessage: string | null;
  analysisExists: boolean | null;
  isCheckingAnalysis: boolean;
  // --- Add Sort State ---
  sortKey: SortKey;
  sortDirection: SortDirection;
  // --- Add Filter State ---
  ratingFilter: RatingFilter;
}

// Constants for polling
const POLLING_INTERVAL_MS = 5000; // 5 seconds
const MAX_POLL_ATTEMPTS = 12; // 12 * 5s = 60 seconds

const SessionHandsPage: React.FC = () => {
  const { playfabId, sessionId } = useParams<{ playfabId: string, sessionId: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<SessionHandsPageState>({
    sessionHands: [],
    loading: true,
    error: null,
    isWaitingForAnalysis: false,
    isTriggeringAnalysis: false,
    analysisTriggerMessage: null,
    analysisExists: null,
    isCheckingAnalysis: false,
    // --- Initialize Sort State ---
    sortKey: 'timestamp', // Default sort
    sortDirection: 'desc',
    // --- Initialize Filter State ---
    ratingFilter: 'all',
  });

  // Ref to store interval ID
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Function to stop polling
    const stopPolling = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        console.log(`SessionHandsPage: Polling stopped for session ${sessionId}.`);
      }
    };

    const pollForHands = async (attempt: number) => {
        if (!playfabId || !sessionId) return; // Should not happen if polling started

        console.log(`SessionHandsPage: Polling attempt ${attempt}/${MAX_POLL_ATTEMPTS} for session ${sessionId}...`);

        try {
            const result = await PlayFab.executeCloudScript('getDynamoDBData', {
              action: 'getSessionHands',
              sessionId: sessionId,
              ownerPlayfabId: playfabId
            });

            const nestedResult = result.data?.FunctionResult?.FunctionResult;

            if (result.success && nestedResult?.success) {
                const handSummaries: HandSummary[] = nestedResult.data;
                if (Array.isArray(handSummaries) && handSummaries.length > 0) {
                    // Success! Hands found.
                    console.log(`SessionHandsPage: Polling successful. Found ${handSummaries.length} hands for session ${sessionId}.`);
                    setState(prevState => ({ // Use prevState update
                      ...prevState,
                      sessionHands: handSummaries,
                      loading: false,
                      error: null,
                      isWaitingForAnalysis: false
                    }));
                    stopPolling(); // Stop polling on success
                    checkAnalysisStatus(); // Check analysis status after hands are found
                    return;
                } else if (Array.isArray(handSummaries) && handSummaries.length === 0) {
                    // Still no hands, continue polling if within limits
                    console.log(`SessionHandsPage: Polling attempt ${attempt} found 0 hands. Continuing...`);
                    if (attempt >= MAX_POLL_ATTEMPTS) {
                        console.log(`SessionHandsPage: Polling timed out for session ${sessionId}.`);
                        setState(prevState => ({
                          ...prevState,
                          loading: false,
                          error: 'Analysis results not found after waiting. Please try again later.',
                          isWaitingForAnalysis: false
                        }));
                        stopPolling();
                    }
                    // No return here, interval continues
                } else {
                    // Unexpected data format during polling
                    console.error('SessionHandsPage: CloudScript returned unexpected data format during polling:', handSummaries);
                    setState(prevState => ({
                       ...prevState,
                       loading: false,
                       error: 'Received invalid data while waiting for analysis.',
                       isWaitingForAnalysis: false
                    }));
                    stopPolling();
                }
            } else {
                // CloudScript error during polling
                const errorMessage = nestedResult?.error || result.data?.FunctionResult?.error || 'Unknown error during polling';
                console.error(`SessionHandsPage: Error during polling attempt ${attempt} for session ${sessionId}:`, errorMessage, nestedResult?.details);
                // Decide whether to stop polling on error or keep trying? Stop for now.
                setState(prevState => ({
                   ...prevState,
                   loading: false,
                   error: `Error waiting for analysis: ${errorMessage}`,
                   isWaitingForAnalysis: false
                }));
                stopPolling();
            }
        } catch (error) {
            console.error(`SessionHandsPage: Network/SDK error during polling attempt ${attempt} for session ${sessionId}:`, error);
            setState(prevState => ({
               ...prevState,
               loading: false,
               error: error instanceof Error ? error.message : 'Network error while waiting for analysis',
               isWaitingForAnalysis: false
            }));
            stopPolling();
        }
    };

    const loadHandsInitial = async () => {
      if (!playfabId || !sessionId) {
        setState(prevState => ({ // Use prevState update
            ...prevState, // Keep existing state like trigger message/state
            sessionHands: [],
            loading: false,
            error: 'User or Session ID missing from URL',
            isWaitingForAnalysis: false
        }));
        return;
      }
      setState(prevState => ({ // Use prevState update
          ...prevState,
          loading: true,
          error: null,
          isWaitingForAnalysis: false,
          // Keep isTriggeringAnalysis and analysisTriggerMessage as they are
      }));
      stopPolling(); // Clear any previous interval
      console.log(`SessionHandsPage: Initial load for session ${sessionId}, user ${playfabId}...`);

      try {
        const result = await PlayFab.executeCloudScript('getDynamoDBData', {
          action: 'getSessionHands',
          sessionId: sessionId,
          ownerPlayfabId: playfabId
        });

        console.log(`SessionHandsPage: Initial CloudScript response for session ${sessionId}, user ${playfabId}:`, result);

        const nestedResult = result.data?.FunctionResult?.FunctionResult;

        if (result.success && nestedResult?.success) {
          const handSummaries: HandSummary[] = nestedResult.data;
          if (Array.isArray(handSummaries) && handSummaries.length > 0) {
            // Hands found on initial load
            console.log(`SessionHandsPage: Successfully parsed ${handSummaries.length} hands on initial load for session ${sessionId}.`);
            setState(prevState => ({ // Use prevState update
              ...prevState,
              sessionHands: handSummaries,
              loading: false,
              error: null,
              isWaitingForAnalysis: false
            }));
            checkAnalysisStatus(); // Check analysis status after successful initial load
          } else if (Array.isArray(handSummaries) && handSummaries.length === 0) {
            // No hands found, START POLLING
            console.log(`SessionHandsPage: No hands found initially for session ${sessionId}. Starting polling...`);
            setState(prevState => ({
                ...prevState,
                sessionHands: [], // Ensure hands are empty
                loading: false, // Stop initial loading indicator
                error: null,
                isWaitingForAnalysis: true // Show waiting message
            }));
            // Start interval
            let attempts = 0;
            pollIntervalRef.current = setInterval(() => {
                attempts++;
                pollForHands(attempts);
            }, POLLING_INTERVAL_MS);

          } else {
            console.error('SessionHandsPage: CloudScript returned unexpected data format on initial load:', handSummaries);
            throw new Error('Invalid data format received for session hands.');
          }
        } else {
          const errorMessage = nestedResult?.error || result.data?.FunctionResult?.error || 'Unknown error fetching session hands';
          console.error(`SessionHandsPage: Error fetching hands initially for session ${sessionId}, user ${playfabId}:`, errorMessage, nestedResult?.details);
           setState(prevState => ({ // Use prevState update
             ...prevState,
             sessionHands: [],
             loading: false,
             error: errorMessage,
             isWaitingForAnalysis: false
           }));
        }
      } catch (error) {
        console.error(`SessionHandsPage: Error in initial loadHands for session ${sessionId}, user ${playfabId}:`, error);
        setState(prevState => ({ // Use prevState update
          ...prevState,
          sessionHands: [],
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to load hands for this session',
          isWaitingForAnalysis: false
        }));
      }
    };

    loadHandsInitial();

    // Cleanup function to clear interval when component unmounts or dependencies change
    return () => {
      stopPolling();
    };

  }, [playfabId, sessionId]); // Reload if either ID changes

  const handleHandClick = (handId: string) => {
    console.log(`SessionHandsPage: Navigating for handId: '${handId}' (Type: ${typeof handId})`);
    navigate(`/history/user/${playfabId}/hand/${handId}`);
  };

  // Analyze Session button handler (unchanged, always triggers)
  const handleAnalyzeSessionClick = async () => {
    // This check should only prevent clicks if analysis is *currently* being triggered
    if (!playfabId || !sessionId || state.isTriggeringAnalysis) {
      console.log(`Analyze button clicked, but blocked by condition: playfabId=${playfabId}, sessionId=${sessionId}, isTriggeringAnalysis=${state.isTriggeringAnalysis}`); // Add log
      return; 
    }
    setState(prevState => ({ ...prevState, isTriggeringAnalysis: true, analysisTriggerMessage: null, error: null }));
    console.log(`Triggering analysis for session ${sessionId}...`);
    try {
      const result = await PlayFab.executeCloudScript('triggerSessionAnalysis', {
        ownerPlayfabId: playfabId,
        sessionId: sessionId,
      });
      const nestedResult = result.data?.FunctionResult?.FunctionResult;
      console.log(`CloudScript response for triggerSessionAnalysis:`, nestedResult); // Log the nested result

      // Handle success (only needs premium check now)
      if (result.success && nestedResult?.success && nestedResult.isPremium === true) { 
          console.log(`Successfully triggered session analysis for ${sessionId}.`);
          setState(prevState => ({
              ...prevState,
              isTriggeringAnalysis: false, // Reset here on success
              analysisTriggerMessage: nestedResult.message || 'Session analysis request sent successfully!', 
          }));
          // Maybe trigger a re-check of status after a delay?
          setTimeout(() => {
              setState(prevState => ({ ...prevState, analysisTriggerMessage: null }));
              // Optionally re-check status so View button appears after generation
              checkAnalysisStatus(); 
          }, 5000);
      } else {
          // Handle error (non-premium, etc.)
          const errorMessage = nestedResult?.error || 'Unknown error triggering analysis';
          console.error(`Error triggering analysis:`, errorMessage);
          setState(prevState => ({
              ...prevState,
              isTriggeringAnalysis: false, // Reset here on error
              error: `Failed to start analysis: ${errorMessage}`, 
              analysisTriggerMessage: null,
          }));
      }
    } catch (error) {
      // Handle network error
      console.error(`Network/SDK error triggering analysis:`, error);
      setState(prevState => ({
        ...prevState,
        isTriggeringAnalysis: false, // Reset here on exception
        error: error instanceof Error ? `Failed to start analysis: ${error.message}` : 'Network error triggering analysis',
        analysisTriggerMessage: null,
      }));
    }
    // Removed finally block as reset is handled in try/catch/success paths
  };

  // --- Function to Check Analysis Status ---
  const checkAnalysisStatus = async () => {
    if (!playfabId || !sessionId) return; 
    if (state.isCheckingAnalysis) return; 

    console.log(`SessionHandsPage: Checking analysis status for session ${sessionId}`);
    setState(prevState => ({ ...prevState, isCheckingAnalysis: true, error: null })); 

    try {
      const result = await PlayFab.executeCloudScript('getDynamoDBData', {
        action: 'getSessionAnalysisData',
        ownerPlayfabId: playfabId,
        sessionId: sessionId
      });

      // Access the inner FunctionResult which contains { success, data: { exists, analysis } }
      const nestedResult = result.data?.FunctionResult?.FunctionResult; 
      console.log("Raw nestedResult from getSessionAnalysisData CloudScript:", nestedResult); // Add log

      if (result.success && nestedResult?.success) {
        // --- CORRECTED ACCESS --- 
        const analysisData = nestedResult.data; // Get the object containing exists/analysis
        console.log("Parsed analysisData from nestedResult:", analysisData); // Add log
        const doesExist = analysisData?.exists; // Access exists property here
        // --- End Correction --- 

        setState(prevState => ({ 
            ...prevState, 
            analysisExists: doesExist, // Use the correctly accessed value
            isCheckingAnalysis: false 
        }));
        console.log(`SessionHandsPage: Analysis status checked. Exists: ${doesExist}`); // Log the actual value being set
      } else {
        const errorMessage = nestedResult?.error || 'Failed to check analysis status';
        console.error("Error checking analysis status:", errorMessage);
        setState(prevState => ({ 
            ...prevState, 
            error: `Error checking analysis status: ${errorMessage}`, 
            isCheckingAnalysis: false, 
            analysisExists: false // Assume not available if check failed
        }));
      }
    } catch (err) {
      console.error("Error calling CloudScript for analysis status check:", err);
      setState(prevState => ({ 
          ...prevState, 
          error: err instanceof Error ? `Error checking analysis status: ${err.message}` : "Network error checking analysis status", 
          isCheckingAnalysis: false, 
          analysisExists: false // Assume not available on error
      }));
    } 
  };
  // --- End Function --- 

  // --- Calculate Totals ---
  const { totalProfitBB, totalHands } = useMemo(() => {
    let profitSum = 0;
    let count = 0;
    if (state.sessionHands && state.sessionHands.length > 0) {
      count = state.sessionHands.length;
      profitSum = state.sessionHands.reduce((sum, hand) => {
        const profit = hand.heroProfitBB ?? 0; // Default to 0 if undefined/null
        return sum + profit;
      }, 0);
    }
    return { totalProfitBB: profitSum, totalHands: count };
  }, [state.sessionHands]);
  // --- End Calculate Totals ---

  // --- Filter and Sort Hands Logic ---
  const filteredAndSortedHands = useMemo(() => {
    if (!state.sessionHands) return [];

    // 1. Filter
    const filtered = state.sessionHands.filter(hand => {
        if (state.ratingFilter === 'all') return true;
        const rating = hand.overallRating ?? hand.summaryJson?.overallRating;

        // Handle simplified categories
        if (typeof rating !== 'number') {
            return false; // Cannot match 'good' or 'excellent' if not a number
        }

        switch(state.ratingFilter) {
            case 'excellent': return rating >= 9;
            case 'good': return rating >= 7 && rating < 9;
            case 'okay': return rating >= 4 && rating < 7;
            case 'poor': return rating >= 1 && rating < 4; // Assuming 0 is not a valid rating, adjust if needed
            // 'all' is handled above, other categories won't be selectable
            default: return false;
        }
    });

    // 2. Sort the filtered list
    const sorted = [...filtered].sort((a, b) => { // Sort the 'filtered' array
      let valA: number | string | Date | null | undefined = null;
      let valB: number | string | Date | null | undefined = null;

      switch (state.sortKey) {
        case 'potSize':
          valA = a.potSize ?? a.summaryJson?.finalPot ?? 0;
          valB = b.potSize ?? b.summaryJson?.finalPot ?? 0;
          break;
        case 'heroProfitBB':
          valA = a.heroProfitBB ?? -Infinity; // Treat missing profit as very low for sorting
          valB = b.heroProfitBB ?? -Infinity;
          break;
        case 'overallRating':
          valA = a.overallRating ?? a.summaryJson?.overallRating ?? -1; // Treat missing rating as low
          valB = b.overallRating ?? b.summaryJson?.overallRating ?? -1;
          break;
        case 'timestamp':
        default:
          valA = new Date(a.timestamp); // Sort by date object
          valB = new Date(b.timestamp);
          break;
      }

      // Handle null/undefined/NaN specifically for numeric types
      if (typeof valA === 'number' && typeof valB === 'number') {
        if (isNaN(valA)) valA = state.sortDirection === 'asc' ? Infinity : -Infinity;
        if (isNaN(valB)) valB = state.sortDirection === 'asc' ? Infinity : -Infinity;
      }
      if (valA instanceof Date && valB instanceof Date) {
         if (isNaN(valA.getTime())) valA = state.sortDirection === 'asc' ? new Date(8640000000000000) : new Date(-8640000000000000);
         if (isNaN(valB.getTime())) valB = state.sortDirection === 'asc' ? new Date(8640000000000000) : new Date(-8640000000000000);
      }

      // --- Corrected Comparison Logic ---
      // Ensure we have comparable values (handles null/undefined/NaN indirectly via initial assignment/handling)
      if (valA == null || valB == null) {
          // Handle cases where one or both might still be null/undefined (should be rare after checks)
          if (valA == null && valB == null) return 0;
          if (valA == null) return state.sortDirection === 'asc' ? -1 : 1; // nulls/undefined first in asc, last in desc
          return state.sortDirection === 'asc' ? 1 : -1; // valB is null/undefined
      }

      if (valA < valB) {
        return state.sortDirection === 'asc' ? -1 : 1;
      }
      if (valA > valB) {
        return state.sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
      // --- End Corrected Comparison Logic ---
    });

    return sorted;
  }, [state.sessionHands, state.sortKey, state.sortDirection, state.ratingFilter]); // Remove numerical filter dependency
  // --- End Filter and Sort Hands Logic ---

  // --- Update Totals Calculation ---
  const { totalProfitBB: filteredTotalProfitBB, totalHands: filteredTotalHands } = useMemo(() => {
    let profitSum = 0;
    let count = 0;
    // Use filteredAndSortedHands for calculations
    if (filteredAndSortedHands && filteredAndSortedHands.length > 0) {
      count = filteredAndSortedHands.length;
      profitSum = filteredAndSortedHands.reduce((sum, hand) => {
        const profit = hand.heroProfitBB ?? 0;
        return sum + profit;
      }, 0);
    }
    return { totalProfitBB: profitSum, totalHands: count };
  }, [filteredAndSortedHands]); // Depend on the filtered & sorted list - Okay
  // --- End Update Totals Calculation ---

  // --- Sorting Handler ---
  const handleSort = (key: SortKey) => {
    setState(prevState => {
      const newDirection = prevState.sortKey === key && prevState.sortDirection === 'asc' ? 'desc' : 'asc';
      return {
        ...prevState,
        sortKey: key,
        sortDirection: newDirection,
      };
    });
  };
  // --- End Sorting Handler ---

  // --- Filtering Handler ---
  const handleFilterChange = (filter: RatingFilter) => {
      setState(prevState => ({
         ...prevState,
         ratingFilter: filter,
      }));
  };

  const { 
      sessionHands, 
      loading, 
      error, 
      isWaitingForAnalysis, 
      isTriggeringAnalysis, 
      analysisTriggerMessage, 
      analysisExists,
      isCheckingAnalysis,
      sortKey,
      sortDirection,
      ratingFilter
    } = state;
  // --- Modify isOwner calculation for robustness --- 
  const loggedInPlayerId = PlayFab.getPlayerId();
  const isOwner = !!loggedInPlayerId && playfabId === loggedInPlayerId;
  // --- End modification --- 

  // --- Define Filter Buttons --- 
  const ratingFilterButtons: { value: RatingFilter; text: string }[] = [
      { value: 'all', text: 'All' },
      { value: 'excellent', text: 'Excellent (9+)' },
      { value: 'good', text: 'Good (7-8)' },
      { value: 'okay', text: 'Okay (4-6)' },
      { value: 'poor', text: 'Poor (1-3)' },
  ];
  // --- End Define Filter Buttons ---

  // Keep Logging 
  console.log(`SessionHandsPage: Checking ownership...`);
  console.log(`  - playfabId from URL: ${playfabId} (Type: ${typeof playfabId})`);
  console.log(`  - Logged in PlayFab ID: ${loggedInPlayerId} (Type: ${typeof loggedInPlayerId})`); // Log the variable used
  console.log(`  - isOwner calculated as: ${isOwner}`);
  // --- 

  // --- Helper for Sort Indicators ---
  const renderSortIndicator = (key: SortKey) => {
    if (sortKey !== key) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };
  // --- End Helper ---

  return (
    <div className="hand-history">
      <div className="hand-history__list" style={{ flex: 'none', width: '100%', maxWidth: '900px', margin: '0 auto' }}>
        <div className="session-hands__header">
          <button onClick={() => navigate(`/history/user/${playfabId}/sessions`)} className="hand-history__back-button">
            &larr; Back to Sessions
          </button>
          
          {/* --- Display Totals --- */}
          {!loading && !isWaitingForAnalysis && filteredTotalHands > 0 ? (
            <div className="session-hands__summary" style={{ alignSelf: 'center', textAlign: 'center', marginLeft: '1rem', marginRight: '1rem', flexGrow: 1 }}>
              <span style={{ marginRight: '1rem' }}>{ratingFilter === 'all' ? 'Total' : 'Filtered'} Hands: {filteredTotalHands}</span>
              <span>
                {ratingFilter === 'all' ? 'Total' : 'Filtered'} Result:
                <span
                  style={{
                    color: filteredTotalProfitBB > 0 ? '#50cd89' : (filteredTotalProfitBB < 0 ? '#f64e60' : '#ffffff'),
                    fontWeight: 'bold',
                    marginLeft: '0.5rem'
                  }}
                >
                  {filteredTotalProfitBB > 0 ? '+' : ''}{filteredTotalProfitBB.toFixed(2)} BB
                </span>
              </span>
            </div>
          ) : null}
          {/* --- End Display Totals --- */}

          {/* --- Combined Button Logic (Restore isOwner check) --- */}
          {!loading && !isWaitingForAnalysis && sessionHands.length > 0 && (
            <div className="session-action-buttons" style={{ display: 'flex', gap: '1rem' }}> 
              {/* Analyze Button: Restore isOwner check */}
              {isOwner && ( 
                <button
                  onClick={handleAnalyzeSessionClick}
                  className="session-hands__analyze-button"
                  disabled={isTriggeringAnalysis}
                >
                  {isTriggeringAnalysis ? 'Starting Analysis...' : 'Analyze Session'}
                </button>
              )}

              {/* View Button: Show only if analysis exists (after check) */}
              {!isCheckingAnalysis && analysisExists === true && (
                <button
                  onClick={() => navigate(`/session-analysis/${playfabId}/${sessionId}`)} 
                  className="session-hands__view-analysis-button" 
                >
                  View Analysis
                </button>
              )}
              
              {/* Optional: Show checking status indicator */}
              {isCheckingAnalysis && (
                 <span className="analysis-status-checking" style={{ alignSelf: 'center' }}>Checking analysis status...</span>
              )}
            </div>
          )}
          {/* --- End Combined Button Logic --- */}
        </div>

        <h2>Hands in Session: {sessionId} {isOwner ? '(Yours)' : ''}</h2>

        {/* --- Add Sort Controls --- */}
        {!loading && !isWaitingForAnalysis && totalHands > 0 && (
          <div className="session-hands__sort-controls" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-start', gap: '1rem', paddingLeft: '1rem' }}>
            <span>Sort by:</span>
            <button onClick={() => handleSort('timestamp')} className={`sort-button ${sortKey === 'timestamp' ? 'active' : ''}`}>
              Time{renderSortIndicator('timestamp')}
            </button>
            <button onClick={() => handleSort('potSize')} className={`sort-button ${sortKey === 'potSize' ? 'active' : ''}`}>
              Pot Size{renderSortIndicator('potSize')}
            </button>
            <button onClick={() => handleSort('heroProfitBB')} className={`sort-button ${sortKey === 'heroProfitBB' ? 'active' : ''}`}>
              Profit (BB){renderSortIndicator('heroProfitBB')}
            </button>
             {/* Optional: Add Overall Rating Sort */}
            <button onClick={() => handleSort('overallRating')} className={`sort-button ${sortKey === 'overallRating' ? 'active' : ''}`}>
              Rating{renderSortIndicator('overallRating')}
            </button>
          </div>
        )}
        {/* --- End Sort Controls --- */}

        {/* --- Simplified Filter Controls --- */}
        {!loading && !isWaitingForAnalysis && state.sessionHands.length > 0 && ( // Use original length check
            <div className="session-hands__filter-controls" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-start', gap: '0.5rem', paddingLeft: '1rem', flexWrap: 'wrap' }}>
                <span>Filter by Rating:</span>
                {ratingFilterButtons.map(filterInfo => (
                    <button
                        key={filterInfo.value}
                        onClick={() => handleFilterChange(filterInfo.value)}
                        className={`filter-button rating-${filterInfo.value} ${ratingFilter === filterInfo.value ? 'active' : ''}`}
                    >
                        {filterInfo.text}
                    </button>
                ))}
            </div>
        )}
        {/* --- End Simplified Filter Controls --- */}

        {analysisTriggerMessage && (
          <div className="session-hands__trigger-message success">{analysisTriggerMessage}</div>
        )}
        {loading ? (
          <div className="hand-history__loading">Loading hands...</div>
        ) : error ? (
          <div className="hand-history__error">{error}</div>
        ) : isWaitingForAnalysis ? (
          <div className="hand-history__loading">Analysis in progress, waiting for results...</div>
        ) : sessionHands.length > 0 ? (
          filteredAndSortedHands.map((handSummary) => {
            const displayTimestamp = handSummary.timestamp;
            const displayPotSize = handSummary.potSize ?? handSummary.summaryJson?.finalPot ?? 'N/A';
            const displayOverallRating = handSummary.overallRating ?? handSummary.summaryJson?.overallRating ?? undefined;
            const displayHeroCardsObj = handSummary.summaryJson?.heroCards ?? handSummary.heroCards ?? null;
            const displayHeroCards = displayHeroCardsObj ? [displayHeroCardsObj.holeCard1, displayHeroCardsObj.holeCard2].filter(Boolean) : [];
            const displayHandType = handSummary.handType ?? handSummary.summaryJson?.metadata?.handType ?? 'Unknown';
            const displayCommunityCards: string[] = handSummary.summaryJson?.board ?? [];
            const displayStreetRatings = handSummary.streetRatings ?? {};
            const displayHeroProfitBB = handSummary.heroProfitBB;

            const overallRatingCategory = displayOverallRating !== undefined ? getRatingCategory(displayOverallRating) : 'unknown';

            const streetOrder: string[] = ['preflop', 'flop', 'turn', 'river'];

            return (
                <div
                    key={handSummary.handId}
                    onClick={() => handleHandClick(handSummary.handId)}
                    className={`hand-history__item`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleHandClick(handSummary.handId); }}
                >
                    <div className="hand-history__item-content">
                        <div className="hand-history__item-row">
                            <div className="hand-history__timestamp">
                                {displayTimestamp ? format(new Date(displayTimestamp), 'PPp') : 'Date N/A'}
                                <span className="hand-history__hand-type">({displayHandType})</span>
                            </div>
                            <div className="hand-history__pot-label">
                                {typeof displayHeroProfitBB === 'number' && (
                                   <span
                                     className="hand-history__profit-loss"
                                     style={{
                                         color: displayHeroProfitBB > 0 ? '#50cd89' : (displayHeroProfitBB < 0 ? '#f64e60' : '#ffffff'),
                                         fontWeight: 'bold',
                                         marginRight: '0.75rem'
                                     }}
                                   >
                                      Result: {displayHeroProfitBB > 0 ? '+' : ''}{displayHeroProfitBB.toFixed(2)} BB
                                   </span>
                                )}
                                Pot: <span className="hand-history__pot">${displayPotSize}</span>
                            </div>
                        </div>

                        <div className="hand-history__item-row hand-history__cards-row">
                            <div className="hand-history__hole-cards">
                                <strong>Hero:</strong>
                                {displayHeroCards.length === 2 ? (
                                    displayHeroCards.map(cardStr => {
                                        const card = parseCard(cardStr);
                                        return card ? <HandHistoryCard key={cardStr} rank={card.rank} suit={card.suit} /> : null;
                                    })
                                ) : <span>--</span>}
                            </div>
                            <div className="hand-history__community-cards">
                                <strong>Board:</strong>
                                {displayCommunityCards.length > 0 ? (
                                    displayCommunityCards.map(cardStr => {
                                        const card = parseCard(cardStr);
                                        return card ? <HandHistoryCard key={cardStr} rank={card.rank} suit={card.suit} /> : null;
                                    })
                                ) : <span>(No board)</span>}
                            </div>
                        </div>

                        <div className="hand-history__item-row hand-history__ratings-row">
                            {displayOverallRating !== undefined && (
                                <div className={`hand-history__overall-rating hand-history__rating--${overallRatingCategory}`}>
                                    Overall: {displayOverallRating}/10
                                </div>
                            )}
                            <div className="hand-history__street-ratings">
                                {streetOrder.map(street => {
                                    const rating = displayStreetRatings[street];
                                    return rating !== undefined && rating !== null ? (
                                        <span key={street} className={`hand-history__street-rating hand-history__rating--${getRatingCategory(rating)}`}>
                                            {street.charAt(0).toUpperCase() + street.slice(1)}: {rating}
                                        </span>
                                    ) : null;
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            );
          })
        ) : (
          <div className="hand-history__empty">
            {state.ratingFilter === 'all'
              ? 'No hands found in this session.' 
              : `No hands found matching the '${ratingFilterButtons.find(b => b.value === state.ratingFilter)?.text ?? state.ratingFilter}' filter.`}
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionHandsPage; 